import { useEffect, useRef, useState, type ElementType, type MouseEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  ChevronLeft,
  ChevronRight,
  Waves,
  Plane,
  Loader2,
  AlertTriangle,
  Users,
  DollarSign,
  TrendingUp,
  Calendar,
  Sparkles,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useDestinations, destinationsQueryOptions, UiDestination } from '@/hooks/useDestinations';
import { RecommendationExplanation } from '@/components/RecommendationExplanation';
import { supabase } from '@/integrations/supabase/client';
import {
  trackEvent,
  beginPlannerSession,
  completePlannerSession,
  endPlannerSession,
  hasActivePlannerSession,
} from '@/lib/analytics';
import { useAuth } from '@/contexts/AuthContext';
import { useSurfLevel, surfLevelToPlannerSkillLevel } from '@/hooks/useSurfProgression';
import {
  type SkillLevel,
  type SurfGoal,
  type TripVibe,
  type Budget,
  type WaterTemp,
  type CrowdTolerance,
} from '@/lib/planner-vocabulary';
import {
  AbilityQuestion,
  SurfGoalQuestion,
  TripVibeQuestion,
  CrowdToleranceQuestion,
  WaterTempQuestion,
  MonthQuestion,
  BudgetQuestion,
} from '@/components/TripPlannerQuestions';
import { MONTHS, SURF_GOAL_OPTIONS, monthFullName, optionLabel, capitalize } from '@/lib/planner-display';
import AdjustPreferencesSheet from '@/components/AdjustPreferencesSheet';

// Measured end-to-end RPC latency (including under ~50 concurrent VUs of
// load) was ~250-700ms -- 8s leaves well over 10x headroom above the worst
// observed case, so this should never fire for a normal request. It exists
// only to stop a genuinely hung request from leaving the user staring at
// "Finding spots..." indefinitely; the client never retries an aborted
// request automatically (postgrest-js's own retry logic explicitly skips
// AbortError), so this can't create a retry storm.
const RPC_TIMEOUT_MS = 8000;

// experienceStyle (Comfortable & Easy / Balanced / Adventure) was retired as
// a separate question -- Comfortable and Adventure now overlap with
// tripVibe's Relaxed and Remote & Adventurous, and Balanced was a neutral
// default rather than a distinct preference. See planner-vocabulary.ts and
// the SQL migration for what stayed and why.
export interface WizardPrefs {
  skillLevel: SkillLevel;
  /** What the surfer wants from the surfing itself. */
  surfGoal: SurfGoal;
  /** What atmosphere they want around the surfing -- an independent signal
   * from surfGoal, not a duplicate of it. */
  tripVibe: TripVibe;
  month: string;
  budget: Budget;
  waterTemp: WaterTemp;
  crowdTolerance: CrowdTolerance;
}

/** Handed to /auth via router state when a signed-out user taps the results
 * screen's account CTA, and handed back on the way to this page once they
 * authenticate (or immediately, if they press Back before finishing) -- lets
 * an already-completed session survive the round trip without re-running the
 * RPC. See handleAccountCtaClick and the mount-time restore below. */
interface PlannerHandoff {
  preferences: WizardPrefs;
  results: UiDestination[];
  resultMeta: Array<{ score: number | null }>;
}

interface TripPlannerWizardProps {
  /** Fires whenever the completed-results state is entered/left, so a
   * parent page can adjust its own chrome (e.g. hiding a mobile-only intro
   * once results are showing) without this component needing to know
   * anything about that parent layout. */
  onResultsChange?: (showingResults: boolean) => void;
}

const TripPlannerWizard = ({ onResultsChange }: TripPlannerWizardProps = {}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { user, loading: authLoading } = useAuth();
  // Mounted for its side effect only (return value unused) -- keeps this
  // query warm from the moment the wizard mounts, so a submission rarely
  // waits on a cold fetch. fetchRecommendationPairs below reads the
  // resolved list via queryClient.ensureQueryData(destinationsQueryOptions)
  // instead of this hook's own return value, so it can never map RPC rows
  // against a stale render-time snapshot -- see that function for why.
  useDestinations();
  // enabled: !!user inside useSurfLevel means this is disabled (isLoading:
  // false, data: undefined) for a signed-out user -- see abilityResolved
  // below for why authLoading is still checked explicitly rather than
  // relying on that alone.
  const { data: storedSurfLevel, isLoading: surfLevelLoading } = useSurfLevel();

  // Captured once, before the mount effect below clears it from history
  // state -- see handleAccountCtaClick for where this is set (both on the
  // current entry, for a Back-button return, and forward to /auth), and
  // Auth.tsx for where it's forwarded on to a successful sign-in/sign-up.
  const [initialHandoff] = useState<PlannerHandoff | null>(
    () => (location.state as { plannerHandoff?: PlannerHandoff } | null)?.plannerHandoff ?? null,
  );

  const [step, setStep] = useState(initialHandoff ? 3 : 1);
  // Mirrors `step` for `beginPlannerSession` below to read at fire time.
  // `beginPlannerSession(() => step)` would look right but isn't: the mount
  // effect that calls it has an empty dependency array (deliberately, so it
  // runs exactly once), so the `() => step` closure it creates freezes on
  // whatever `step` equalled at that first render and never sees later
  // `setStep` calls -- a stale closure, not a live read. A ref's `.current`
  // is mutable storage rather than a captured binding, so updating it via
  // the effect below and reading `stepRef.current` from the session's
  // getter always reflects the step the user was actually on when they
  // left, regardless of when the getter closure itself was created.
  const stepRef = useRef(step);
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState<UiDestination[] | null>(initialHandoff?.results ?? null);
  const [resultMeta, setResultMeta] = useState<Array<{ score: number | null }>>(
    initialHandoff?.resultMeta ?? [],
  );
  // Distinct from "results is [] " (a genuine zero-match search that
  // completed successfully) — this means the RPC itself never returned
  // usable data. Keeping these separate is what lets the results
  // step tell "we searched and found nothing" apart from "the search
  // failed and never ran."
  const [hasError, setHasError] = useState(false);
  // Distinguishes "the request timed out" from other failures so the error
  // panel can show a more accurate, less alarming message -- a slow
  // response isn't the same situation as a genuine server/network error.
  const [isTimeout, setIsTimeout] = useState(false);
  // Focus target for the error/retry state below. A sighted keyboard user
  // or screen reader user needs to land here the moment the error appears
  // (or reappears after a failed retry) instead of focus staying wherever
  // it was — see the effect keyed on [hasError, isLoading] further down.
  const errorRef = useRef<HTMLDivElement>(null);

  // Adjust Preferences editor state -- deliberately separate from
  // isLoading/hasError/isTimeout above (see handleRefine's header comment):
  // a failed refinement must never trip the initial-submission error screen
  // or its focus-management effect, so it gets its own small state slice
  // that only AdjustPreferencesSheet reads.
  const [isAdjustOpen, setIsAdjustOpen] = useState(false);
  const [isRefining, setIsRefining] = useState(false);
  const [refineError, setRefineError] = useState<string | null>(null);

  const [preferences, setPreferences] = useState<WizardPrefs>(
    initialHandoff?.preferences ?? {
      skillLevel: 'intermediate',
      surfGoal: 'score',
      tripVibe: 'social',
      month: MONTHS[new Date().getMonth()],
      budget: '$$',
      waterTemp: 'any',
      crowdTolerance: 'some',
    },
  );

  // Whether there's enough information yet to decide which ability UI to
  // show: either a confirmed-signed-out user (no profile to wait on) or a
  // signed-in user whose stored surf_level query has settled (success,
  // empty, or error -- any of those is a real answer, not silence). Checked
  // explicitly rather than relying on useSurfLevel's own isLoading alone,
  // because that's false for a *disabled* query too (TanStack Query only
  // reports isLoading while actively fetching) -- during the brief window
  // right after mount where AuthContext itself is still resolving
  // getSession() and `user` is still null, useSurfLevel would look
  // "not loading" purely because it hasn't been allowed to start yet, which
  // would flash the signed-out picker before the real signed-in state (and
  // possibly a stored level) arrives a moment later.
  const abilityResolved = !authLoading && (!user || !surfLevelLoading);
  const profileSkillLevel = storedSurfLevel ? surfLevelToPlannerSkillLevel(storedSurfLevel) : null;
  // Once resolved, a signed-in user with a real stored level gets the
  // collapsed summary instead of the full picker -- the actual "remove a
  // decision we already know the answer to" behaviour, not just a
  // pre-selected default. Signed-out users and signed-in users who never
  // set a level (profileSkillLevel === null) keep today's full picker,
  // unchanged.
  const showAbilitySummary = abilityResolved && Boolean(user) && profileSkillLevel !== null;
  // Lets a summary row expand into the full picker in place. Independent of
  // showAbilitySummary so a manual "Change" tap can override it for this
  // session without touching the stored profile value: AbilityQuestion's
  // full-picker branch (TripPlannerQuestions.tsx) is driven by the same
  // value/onChange -> updatePref('skillLevel', ...) pair regardless of which
  // branch renders it, so a manual pick there simply overwrites
  // preferences.skillLevel in local state -- never a write back to
  // user_profiles.surf_level.
  const [abilityManuallyExpanded, setAbilityManuallyExpanded] = useState(false);
  // Guards the auto-apply effect below so it fires (at most) once per mount
  // -- otherwise a later refetch of the surf-level query (e.g. window
  // refocus) could silently stomp a manual selection the user already made
  // in the expanded picker.
  const appliedProfileSkillLevelRef = useRef(false);

  useEffect(() => {
    // A resumed, already-completed session (initialHandoff) carries whatever
    // skillLevel actually produced its results -- possibly a manual choice
    // made before profile data was even relevant. Never overwrite that with
    // a fresh profile lookup; this pre-fill only applies to a genuinely new
    // attempt.
    if (initialHandoff) return;
    if (appliedProfileSkillLevelRef.current) return;
    if (!abilityResolved) return;
    appliedProfileSkillLevelRef.current = true;
    if (profileSkillLevel) {
      setPreferences((prev) => ({ ...prev, skillLevel: profileSkillLevel }));
    }
  }, [initialHandoff, abilityResolved, profileSkillLevel]);

  // Starts (or restarts) planner_started/session tracking for a fresh
  // attempt. Shared by the mount effect below and handleReset, so that
  // "Adjust Preferences" after a completion -- whether that completion
  // happened in this same mount or was handed back from a completed session
  // restored via plannerHandoff -- gets its own clean planner_started, and
  // therefore correct planner_abandoned/planner_completed coverage, rather
  // than silently reusing (or never re-arming) a session that's already
  // closed. beginPlannerSession's own re-entrancy guard (see analytics.ts)
  // already handles the "called while a session is still open" case --
  // e.g. adjusting preferences straight from an RPC error, where the
  // original session never closed -- so this is only ever invoked when
  // hasActivePlannerSession() is false, to avoid firing a spurious extra
  // "restart" abandonment for a session that didn't need restarting.
  const startPlannerSession = () => {
    trackEvent('planner_started', { current_page: window.location.pathname });
    beginPlannerSession(() => stepRef.current);
  };

  useEffect(() => {
    // This effect intentionally has an empty dependency array so it runs
    // exactly once, on mount. `initialHandoff` was itself captured once via
    // a useState initializer above for the same reason, so both agree for
    // this mount's entire lifetime on whether it's resuming an
    // already-completed session. Do NOT add `location.state` or
    // `initialHandoff` to the dependency array to satisfy an
    // exhaustive-deps lint warning: `initialHandoff` never changes after
    // mount, so it would be a no-op for its own sake, but it would also
    // invite a future edit to re-run the session-arming logic below on some
    // later, unrelated location.state change -- reintroducing duplicate
    // planner_started/planner_abandoned firing for the same resumed
    // session. This is deliberately NOT the same pattern as
    // MyPassport.tsx's openAdd flag: that effect depends on
    // `[location.state]` and re-checks on every change, which is fine there
    // because opening the add sheet is idempotent and has no
    // session-tracking consequence -- unlike beginPlannerSession here.
    if (initialHandoff) {
      navigate(location.pathname, { replace: true, state: null });
    } else {
      startPlannerSession();
    }
    // Registered unconditionally (not nested inside the branch above) so a
    // session armed later -- e.g. by handleReset, after this mount resumed
    // via plannerHandoff with no session of its own yet -- is still
    // correctly closed out on unmount. endPlannerSession is always safe to
    // call even when no session is open (it no-ops).
    return () => {
      endPlannerSession(true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keeps stepRef live for the mount effect above -- see stepRef's own
  // comment for why a plain closure over `step` can't do this itself.
  useEffect(() => {
    stepRef.current = step;
  }, [step]);

  const updatePref = <K extends keyof WizardPrefs>(key: K, value: WizardPrefs[K]) => {
    setPreferences((prev) => ({ ...prev, [key]: value }));
  };

  // Moves focus to the error state whenever it becomes the visible state —
  // both the first time the RPC fails and again after a failed retry.
  // `hasError` alone doesn't change on a failed retry (it's already true),
  // so this also keys off `isLoading` going false while `hasError` is still
  // true, which is exactly the moment the retry attempt just finished badly.
  useEffect(() => {
    if (hasError && !isLoading) {
      errorRef.current?.focus();
    }
  }, [hasError, isLoading]);

  useEffect(() => {
    onResultsChange?.(Boolean(results) && step === 3);
  }, [results, step, onResultsChange]);

  // The one piece of RPC/result-mapping logic genuinely shared between an
  // initial submission and a later refinement from the results screen --
  // everything else (what happens to `results`/`hasError` on success or
  // failure, which analytics event fires, whether `step` changes) stays in
  // each caller below, because those two situations deliberately behave
  // differently (see handleRefine's header comment for why a failed
  // refinement must never touch the existing result set the way a failed
  // initial submission does).
  const fetchRecommendationPairs = async (
    prefs: WizardPrefs,
  ): Promise<Array<{ dest: UiDestination; score: number | null }>> => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await (supabase as any)
      .rpc('get_top_destination_matches_v3', {
        p_month: prefs.month,
        p_skill_level: prefs.skillLevel,
        p_budget: prefs.budget,
        p_crowd_level: prefs.crowdTolerance,
        p_water_temp: prefs.waterTemp,
        p_wave_type: null,
        p_vibe: null,
        p_trip_goal: prefs.surfGoal,
        // p_experience_style deliberately omitted (defaults to NULL) -- the
        // wizard no longer asks this question; see WizardPrefs' header
        // comment for why. The RPC parameter itself stays live server-side.
        p_trip_vibe: prefs.tripVibe,
      })
      .abortSignal(AbortSignal.timeout(RPC_TIMEOUT_MS));

    if (error) throw error;

    // Resolve the destination list needed to map RPC rows to display data
    // before mapping anything -- awaits the in-flight fetch if one is
    // already running, returns cached data if still fresh, or triggers and
    // awaits a fresh fetch otherwise (same query, same retry/retryDelay as
    // useDestinations() above, via the shared destinationsQueryOptions).
    // Never reads a render-time closure over possibly-still-loading data:
    // that was the bug (a submission racing the destinations query's first
    // load, or landing after it had already failed, silently mapped every
    // RPC row to nothing and rendered a false "zero matches" success). A
    // failure here throws and propagates to this function's callers'
    // existing error handling (handleSubmit's hasError screen /
    // handleRefine's refineError) instead of being swallowed into an empty
    // result set.
    const resolvedDestinations = await queryClient.ensureQueryData(destinationsQueryOptions);

    // Map RPC rows to UiDestination by slug lookup, keeping the score alongside.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const rows: any[] = Array.isArray(data) ? data : [];
    const pairs = rows.slice(0, 3).map((row) => {
      const slug = row.slug ?? row.destination_slug ?? row.id;
      const dest = resolvedDestinations.find((d) => d.id === slug);
      const score = row.overall_match_score ?? row.match_score ?? row.score ?? null;
      return dest ? { dest, score: typeof score === 'number' ? score : null } : null;
    });

    // Once the destination list is genuinely resolved (not mid-load), every
    // RPC row should match one -- the RPC's own is_active filter mirrors
    // destinationsQueryOptions' `.eq('is_active', true)`. An unmatched row
    // here means a real invariant violation (e.g. a destination deactivated
    // in the narrow gap between the RPC's snapshot and this query's cached
    // one), not the race this fix closes.
    const matched = pairs.filter((p): p is { dest: UiDestination; score: number | null } => !!p);
    const unmatched = pairs.length - matched.length;

    if (rows.length > 0 && matched.length === 0) {
      // Every returned row failed to match -- this is no longer "one stale
      // slug," it's the whole result set silently disappearing, which is
      // exactly the false empty-success state this fix exists to prevent.
      // Throwing here (rather than returning []) propagates into this
      // function's callers' existing error handling (handleSubmit's
      // hasError screen / handleRefine's refineError) instead of being
      // indistinguishable from a genuine "we searched and found nothing"
      // result -- see rows.length === 0 below for that real case, which
      // this branch deliberately does not touch.
      throw new Error(
        `fetchRecommendationPairs: RPC returned ${rows.length} row(s) but none matched the loaded destination list`,
      );
    }

    if (unmatched > 0) {
      // A partial mismatch, by contrast, still has genuine, valid matches
      // to show -- log it for diagnosis and drop just the unmatched rows,
      // since discarding the whole search over one stale slug would be a
      // worse outcome than one missing card.
      console.warn(
        `fetchRecommendationPairs: ${unmatched} of ${pairs.length} RPC row(s) had no matching entry in the loaded destination list`,
      );
    }

    return matched;
  };

  // postgrest-js catches the raw fetch error and re-wraps it into a plain
  // {message, details, hint, code} object -- the original DOMException's
  // `.name` doesn't survive onto what a caller catches (confirmed by logging
  // the actual shape: {message: "TimeoutError: signal timed out", ...}), so
  // detection has to match on the message string postgrest-js builds
  // (`${fetchError.name}: ...`) rather than a `.name` property.
  // AbortSignal.timeout() throws with name 'TimeoutError'; a
  // manually-aborted AbortController would be 'AbortError' -- checking both
  // covers either path. Nothing else fetchRecommendationPairs's call
  // produces a message starting with either.
  const isAbortTimeoutError = (err: unknown): boolean => {
    const errMessage = String((err as { message?: unknown } | null)?.message ?? '');
    return errMessage.startsWith('TimeoutError') || errMessage.startsWith('AbortError');
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    // Deliberately not cleared here: if this is a retry from the error
    // screen, staying `true` through the in-flight request keeps that
    // screen (with its spinner) on-screen instead of flashing back to the
    // bare wizard shell for the split second before the RPC resolves. It's
    // cleared below on the success path, or re-affirmed on failure.
    try {
      const pairs = await fetchRecommendationPairs(preferences);
      const mapped = pairs.map((p) => p.dest);
      setResults(mapped);
      setResultMeta(pairs.map((p) => ({ score: p.score })));
      setHasError(false);
      setIsTimeout(false);
      completePlannerSession();
      trackEvent('planner_completed', {
        skill_level: preferences.skillLevel,
        surf_goal: preferences.surfGoal,
        trip_vibe: preferences.tripVibe,
        month: preferences.month,
        budget: preferences.budget,
        water_temp: preferences.waterTemp,
        crowd_tolerance: preferences.crowdTolerance,
        result_count: mapped.length,
        top_destination: mapped[0]?.name ?? null,
      });
    } catch (err) {
      console.error('Trip match RPC failed:', err);
      // Keep results as null (not []) on failure — [] means "we searched
      // and genuinely found nothing," which is a different, valid state
      // from "the search itself never completed".
      setResults(null);
      setResultMeta([]);
      setHasError(true);
      setIsTimeout(isAbortTimeoutError(err));
    } finally {
      setIsLoading(false);
      setStep(3);
    }
  };

  // Refining from the results screen is a deliberately different UX/state
  // transition from the initial submission above, not a second copy of it:
  // handleSubmit's failure branch nulls `results` because there's nothing
  // valid to preserve on a first attempt, but that exact behaviour would be
  // a regression here -- a transient network blip while adjusting one
  // answer must never destroy an already-good result set the user is still
  // looking at. So this never touches `results`/`resultMeta`/`hasError`/
  // `isTimeout`/`step` on failure; only AdjustPreferencesSheet's own
  // `refineError` state changes, and the sheet stays open with the old
  // results still visible underneath it. `preferences` (the committed
  // source of truth the summary/results are derived from) is likewise only
  // ever written to on the success path below -- never speculatively.
  const handleRefine = async (updated: WizardPrefs, changedFields: Array<keyof WizardPrefs>) => {
    setIsRefining(true);
    setRefineError(null);
    trackEvent('planner_adjust_requested', { changed_fields: changedFields });
    try {
      const pairs = await fetchRecommendationPairs(updated);
      const mapped = pairs.map((p) => p.dest);
      setResults(mapped);
      setResultMeta(pairs.map((p) => ({ score: p.score })));
      setPreferences(updated);
      setIsAdjustOpen(false);
      trackEvent('planner_adjust_applied', {
        changed_fields: changedFields,
        skill_level: updated.skillLevel,
        surf_goal: updated.surfGoal,
        trip_vibe: updated.tripVibe,
        month: updated.month,
        budget: updated.budget,
        water_temp: updated.waterTemp,
        crowd_tolerance: updated.crowdTolerance,
        result_count: mapped.length,
        top_destination: mapped[0]?.name ?? null,
      });
    } catch (err) {
      console.error('Trip refinement RPC failed:', err);
      setRefineError("Couldn't update your matches. Please try again.");
      trackEvent('planner_adjust_failed', {
        changed_fields: changedFields,
        is_timeout: isAbortTimeoutError(err),
      });
    } finally {
      setIsRefining(false);
    }
  };

  const handleReset = () => {
    setResults(null);
    setResultMeta([]);
    setHasError(false);
    setIsTimeout(false);
    setStep(1);
    // A genuinely new attempt starting here needs its own session so it gets
    // correct planner_abandoned/planner_completed coverage. Only arm one if
    // none is currently open -- see startPlannerSession's header comment for
    // why: this is reachable both from a normal completed-results screen
    // (whose session already closed via completePlannerSession) and from an
    // RPC-error screen (whose session is still open and must be left alone).
    if (!hasActivePlannerSession()) {
      startPlannerSession();
    }
  };

  // What actually happens on the results screen's account CTA differs by
  // auth state, and neither branch literally saves this specific
  // result set anywhere: there's no "saved trip" entity in the schema today
  // (only single-destination saves, via useToggleSavedDestination on
  // DestinationDetail.tsx, keyed by a database id this component's
  // UiDestination results don't carry -- only their slug). So the CTA is
  // deliberately worded as an honest account-oriented action rather than a
  // save that doesn't happen: a signed-in user already has somewhere to
  // manage saved destinations (My Trips); a signed-out user is offered the
  // account that unlocks that -- matching Auth.tsx's own "Save
  // destinations, track your trips" copy -- not a promise that this click
  // saves anything by itself.
  const handleAccountCtaClick = () => {
    trackEvent('planner_account_cta_clicked', {
      signed_in: Boolean(user),
      destination: user ? 'my-trips' : 'auth',
    });
    if (user) {
      navigate('/my-trips');
      return;
    }
    // Only rendered once results exist (see the results-screen block below),
    // but guarded here too since results is nullable in this component's
    // state shape.
    if (!results) return;
    const handoff: PlannerHandoff = { preferences, results, resultMeta };
    // Stamp the *current* /trip-planner history entry with the same
    // restorable state before leaving, so pressing the browser Back button
    // from /auth returns here with results intact. Without this, Back would
    // land on whatever entry this page had *before* results existed (its
    // location.state never carried a handoff, since that state didn't exist
    // yet), remounting the wizard fresh at Stage 1. This is a same-path
    // replace (no route change), so it does not remount or otherwise affect
    // the currently-rendered wizard -- it only rewrites the history entry a
    // future back-navigation would land on, using the exact same
    // plannerHandoff shape/restore path a forward handoff already uses (see
    // initialHandoff above), not a new mechanism.
    navigate(location.pathname, { replace: true, state: { plannerHandoff: handoff } });
    navigate('/auth', {
      state: {
        from: '/trip-planner',
        initialTab: 'signup',
        plannerHandoff: handoff,
      },
    });
  };

  const steps = [
    { num: 1, title: 'Your Surfing', icon: Waves },
    { num: 2, title: 'Trip Logistics', icon: Plane },
  ];

  if (step === 3 && hasError) {
    return (
      <div
        ref={errorRef}
        role="alert"
        tabIndex={-1}
        className="max-w-lg mx-auto text-center space-y-6 md:space-y-8 px-4 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10">
          <AlertTriangle className="h-7 w-7 text-destructive" />
        </div>
        <div className="space-y-2">
          <h2 className="font-serif text-2xl md:text-3xl font-semibold text-foreground">
            We couldn't find your matches
          </h2>
          <p className="text-sm md:text-base text-muted-foreground">
            {isTimeout
              ? 'This is taking longer than expected. Your preferences are still saved — try again.'
              : 'Something went wrong while searching for destinations. Your preferences are still saved — try again, or adjust them and search again.'}
          </p>
        </div>
        {/* Focus lands on the container above and announces the heading +
            message immediately. This separate live region covers the one
            state change that happens without a focus move — the retry
            button's label switching to "Trying again..." while focus stays on
            the button itself. Scoped to this error state rather than routed
            through the shared toast system (src/hooks/use-toast.ts), since a
            toast is for a transient notification and this is a persistent,
            already-visible page state — a local live region is the smaller,
            more correct fix for this one call site. */}
        <span aria-live="polite" className="sr-only">
          {isLoading ? 'Trying again...' : ''}
        </span>
        <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center pt-2">
          <Button variant="outline" onClick={handleReset} className="w-full sm:w-auto">
            Adjust Preferences
          </Button>
          <Button variant="default" onClick={handleSubmit} disabled={isLoading} className="w-full sm:w-auto gap-2">
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Trying again...
              </>
            ) : (
              'Try Again'
            )}
          </Button>
        </div>
      </div>
    );
  }

  if (results && step === 3) {
    const podiumOrder = results.length >= 3 ? [results[1], results[0], results[2]] : results;

    // Compact primary summary -- deliberately only 4 of the 7 answers
    // (Ability, Month, Budget, Surf Goal), per the Hick's Law brief: the
    // destination cards below stay the visual priority, and all 7 remain
    // fully visible/editable inside Adjust Preferences (see
    // AdjustPreferencesSheet.tsx). Read-only, plain text -- no tap targets
    // of its own, so "Adjust Preferences" stays the one editing entry point.
    const primarySummary = [
      capitalize(preferences.skillLevel),
      monthFullName(preferences.month),
      preferences.budget,
      optionLabel(SURF_GOAL_OPTIONS, preferences.surfGoal),
    ].join(' · ');

    const handleOpenAdjust = () => {
      trackEvent('planner_adjust_opened', {});
      setIsAdjustOpen(true);
    };

    // Refuses to close while a refinement request is in flight -- ESC,
    // outside-click, and the drawer's own swipe-to-dismiss all funnel
    // through this same onOpenChange, so guarding here (rather than only
    // disabling the "Update My Matches" button) is what actually keeps the
    // editor open for the RPC's whole duration, not just against one
    // dismiss path.
    const handleAdjustOpenChange = (open: boolean) => {
      if (isRefining) return;
      setIsAdjustOpen(open);
    };

    const handleRecommendationClick = (dest: UiDestination, rank: number) => {
      const score = resultMeta[rank - 1]?.score ?? null;
      trackEvent('recommendation_clicked', {
        destination_name: dest.name,
        destination_slug: dest.id,
        recommendation_rank: rank,
        overall_match_score: score,
      });
      navigate(`/destinations/${dest.id}`, { state: { preferences } });
    };

    return (
      <div className="space-y-6 md:space-y-8">
        {/* md:mt-4 lg:mt-6 only: desktop/tablet keep the "Plan Your Surf
            Trip" intro visible above this (TripPlanner.tsx), unlike mobile
            which hides it once results are showing -- these two steps sat
            too close together without it. Mobile is untouched since there's
            no bare `mt-*` here. */}
        <div className="text-center space-y-2 px-4 md:mt-4 lg:mt-6">
          <h2 className="font-serif text-2xl md:text-3xl font-semibold text-foreground">Your Perfect Matches</h2>
          <p className="text-sm md:text-base text-muted-foreground">Based on your preferences, we recommend these destinations</p>
          <div className="pt-1 space-y-0.5">
            <p className="text-sm font-medium text-foreground">{primarySummary}</p>
            <p className="text-xs text-muted-foreground">Based on 7 trip preferences</p>
          </div>
        </div>

        {/* Mobile Layout — vertically stacked ranked list */}
        <MobileRecommendationList
          results={results}
          preferences={preferences}
          onClick={handleRecommendationClick}
        />

        {/* Desktop Podium -- a self-contained card, not SurfSpotCard (that
            component is shared with the Destinations listing/carousel and
            tuned for a ~150-256px-wide context; reusing it here was what
            produced the too-tall-and-narrow cards and unreadable stat text).
            Mirrors MobileRecommendationList's own already-working pattern
            of owning its markup instead of fighting a shared component. */}
        <div className="hidden md:block">
          <div className="mx-auto grid max-w-6xl grid-cols-3 items-start gap-6 px-4 lg:gap-8">
            {podiumOrder.map((dest, idx) => {
              const originalRank = results.length >= 3 ? (idx === 0 ? 2 : idx === 1 ? 1 : 3) : idx + 1;
              const isFirst = originalRank === 1;
              const meta = RANK_META[originalRank - 1] ?? RANK_META[2];

              const goToDestination = (e: MouseEvent) => {
                e.stopPropagation();
                handleRecommendationClick(dest, originalRank);
              };

              return (
                // The podium "lift" is a top-padding spacer on the two side
                // cards, not a negative margin pulling the center one up --
                // negative margin lost to this grid's row auto-sizing when
                // tried (the row just grew to absorb it, visually
                // cancelling the effect; verified by checking rendered
                // bounding boxes, not assumed). Pushing the sides down is
                // the same "center is highest" result without fighting the
                // grid algorithm.
                <div key={dest.id} className={cn(!isFirst && 'pt-6 lg:pt-10')}>
                <article
                  onClick={() => handleRecommendationClick(dest, originalRank)}
                  className={cn(
                    'cursor-pointer overflow-hidden rounded-2xl border bg-card transition-shadow hover:shadow-lg',
                    isFirst ? 'border-primary/30 shadow-lg' : 'border-border/50 shadow-sm',
                  )}
                >
                  <div className="relative aspect-[4/3] w-full overflow-hidden">
                    <img src={dest.image} alt={dest.name} className="h-full w-full object-cover" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/10 to-transparent" />

                    {/* One badge carries both the rank and the Best Match /
                        Strong Alternative / Worth Considering label -- the
                        separate floating rank circle and "Best Match" pill
                        this replaces were two overlapping indicators doing
                        one job. Same RANK_META the mobile list already uses,
                        so the two breakpoints read as the same product. */}
                    <div className="absolute left-3 top-3">
                      <div
                        className={cn(
                          'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold shadow-sm',
                          meta.badge,
                        )}
                      >
                        <span className="text-sm leading-none">{meta.medal}</span>
                        <span>{meta.label}</span>
                      </div>
                    </div>

                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <h3 className="font-serif text-xl font-semibold text-foreground leading-tight lg:text-2xl">
                        {dest.name}
                      </h3>
                      <p className="mt-0.5 text-xs text-muted-foreground">{dest.country}</p>
                    </div>
                  </div>

                  <div className="space-y-4 p-4 lg:p-5">
                    <div className="grid grid-cols-3 gap-2">
                      <DesktopStat icon={Sparkles} label="Experience" value={dest.skillLabel ?? capitalize(dest.skillLevel)} />
                      <DesktopStat icon={Waves} label="Wave Type" value={capitalize(dest.waveType)} />
                      <DesktopStat icon={Users} label="Crowd" value={capitalize(dest.crowdLevel)} />
                      <DesktopStat icon={DollarSign} label="Cost" value={'$'.repeat(dest.costRating)} />
                      <DesktopStat icon={TrendingUp} label="Consistency" value={`${dest.consistency}/10`} />
                      <DesktopStat icon={Calendar} label="Best Months" value={dest.bestMonths.slice(0, 2).join(', ')} />
                    </div>

                    <RecommendationExplanation destination={dest} preferences={preferences} />

                    {/* The one explicit call-to-action -- SurfSpotCard's own
                        "Explore Destination" button and the separate "View
                        destination" link this replaced both did the same
                        navigation as the card itself; this is the only one
                        left, plus the card remains clickable as a larger
                        target (stopPropagation keeps that from double-firing
                        the click). */}
                    <Button type="button" onClick={goToDestination} className="w-full">
                      View destination →
                    </Button>
                  </div>
                </article>
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center pt-2 md:pt-4 px-4">
          <Button variant="outline" onClick={handleOpenAdjust} className="w-full sm:w-auto">
            Adjust Preferences
          </Button>
          <Button variant="default" className="w-full sm:w-auto" onClick={handleAccountCtaClick}>
            {user ? 'View My Trips' : 'Create Account to Save Trips'}
          </Button>
        </div>

        <AdjustPreferencesSheet
          open={isAdjustOpen}
          onOpenChange={handleAdjustOpenChange}
          preferences={preferences}
          isSubmitting={isRefining}
          errorMessage={refineError}
          onSubmit={handleRefine}
        />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6 md:space-y-8 px-2 md:px-0">
      {/* Progress Steps */}
      <div className="flex items-center justify-center gap-1 sm:gap-2">
        {steps.map((s, idx) => (
          <div key={s.num} className="flex items-center">
            <button
              onClick={() => step > s.num && setStep(s.num)}
              disabled={step < s.num}
              className={cn(
                'flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 rounded-full transition-all text-xs sm:text-sm',
                step === s.num
                  ? 'bg-primary text-primary-foreground'
                  : step > s.num
                    ? 'bg-primary/20 text-primary cursor-pointer hover:bg-primary/30'
                    : 'bg-muted text-muted-foreground',
              )}
            >
              <s.icon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline font-medium">{s.title}</span>
              <span className="sm:hidden font-medium">{s.num}</span>
            </button>
            {idx < steps.length - 1 && (
              <div className={cn('w-4 sm:w-8 h-0.5 mx-1 sm:mx-2', step > s.num ? 'bg-primary/50' : 'bg-muted')} />
            )}
          </div>
        ))}
      </div>

      {/* Step Content */}
      <Card className="border-border/50 shadow-lg">
        <CardHeader>
          <CardTitle className="font-serif text-xl">
            {step === 1 && 'Tell us about your surfing'}
            {step === 2 && 'Your trip logistics'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {step === 1 && (
            <>
              {/* Ability, surf goal, trip vibe, and crowd tolerance are all
                  self/preference questions -- none depend on a calendar or a
                  budget, which is why they're grouped in this stage (crowd
                  tolerance moved here from its own standalone step -- see
                  the Hick's Law friction audit). Extracted into
                  TripPlannerQuestions.tsx so the same controls can be reused
                  by a future Adjust Preferences editor without duplicating
                  this markup. */}
              <AbilityQuestion
                abilityResolved={abilityResolved}
                showSummary={showAbilitySummary && !abilityManuallyExpanded}
                profileSkillLevel={profileSkillLevel}
                onRequestChange={() => setAbilityManuallyExpanded(true)}
                value={preferences.skillLevel}
                onChange={(v) => updatePref('skillLevel', v)}
              />
              <SurfGoalQuestion value={preferences.surfGoal} onChange={(v) => updatePref('surfGoal', v)} />
              <TripVibeQuestion value={preferences.tripVibe} onChange={(v) => updatePref('tripVibe', v)} />
              <CrowdToleranceQuestion
                value={preferences.crowdTolerance}
                onChange={(v) => updatePref('crowdTolerance', v)}
              />
            </>
          )}

          {step === 2 && (
            <>
              {/* Month, budget, and water temp are all external-world trip
                  constraints, not surfer traits -- grouped separately from
                  Stage 1 for that reason. Extracted into
                  TripPlannerQuestions.tsx, same rationale as Stage 1 above. */}
              <MonthQuestion value={preferences.month} onChange={(v) => updatePref('month', v)} />
              <BudgetQuestion value={preferences.budget} onChange={(v) => updatePref('budget', v)} />
              <WaterTempQuestion value={preferences.waterTemp} onChange={(v) => updatePref('waterTemp', v)} />
            </>
          )}

          {/* Navigation */}
          <div className="flex justify-between pt-4">
            <Button variant="outline" onClick={() => setStep(step - 1)} disabled={step === 1} className="gap-2">
              <ChevronLeft className="h-4 w-4" />
              Back
            </Button>

            {step < 2 ? (
              <Button onClick={() => setStep(step + 1)} className="gap-2">
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            ) : (
              <Button onClick={handleSubmit} disabled={isLoading} className="gap-2">
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Finding spots...
                  </>
                ) : (
                  <>
                    Find My Spots
                    <Waves className="h-4 w-4" />
                  </>
                )}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TripPlannerWizard;

interface MobileListProps {
  results: UiDestination[];
  preferences: WizardPrefs;
  onClick: (dest: UiDestination, rank: number) => void;
}

const RANK_META = [
  {
    medal: '🥇',
    label: 'Best Match',
    badge: 'bg-yellow-400 text-yellow-900',
    ring: 'ring-2 ring-primary/40 shadow-xl',
  },
  {
    medal: '🥈',
    label: 'Strong Alternative',
    badge: 'bg-gray-200 text-gray-800',
    ring: 'shadow-md',
  },
  {
    medal: '🥉',
    label: 'Worth Considering',
    badge: 'bg-amber-600 text-amber-50',
    ring: 'shadow-md',
  },
] as const;

/** Desktop-podium-only stat cell. Deliberately not the shared SurfSpotCard
 * StatBox -- that one's sized for a ~150-256px card and bottoms out at
 * 6-7px text, which is unreadable. The desktop podium card has roughly
 * 350px+ to work with, so this gets a real minimum readable size instead. */
const DesktopStat = ({ icon: Icon, label, value }: { icon: ElementType; label: string; value: string }) => (
  <div className="flex flex-col items-center justify-center gap-1 rounded-lg bg-muted/50 px-2 py-2.5 text-center">
    <Icon className="h-3.5 w-3.5 text-primary" aria-hidden />
    <span className="text-xs font-semibold leading-tight text-foreground">{value}</span>
    <span className="text-[10px] uppercase leading-tight tracking-wide text-muted-foreground">{label}</span>
  </div>
);

const MobileRecommendationList = ({ results, preferences, onClick }: MobileListProps) => {
  return (
    <div className="md:hidden space-y-6 px-1">
      {results.map((dest, idx) => {
        const rank = idx + 1;
        const meta = RANK_META[idx] ?? RANK_META[2];

        return (
          <article
            key={dest.id}
            className={cn(
              'relative overflow-hidden rounded-2xl bg-card border border-border/50',
              meta.ring,
            )}
          >
            {/* Image, with rank badge + counter overlaid directly on the photo
                (no separate header strip above it) */}
            <button
              type="button"
              onClick={() => onClick(dest, rank)}
              className="block w-full text-left"
            >
              <div className="relative aspect-[16/10] w-full overflow-hidden">
                <img
                  src={dest.image}
                  alt={dest.name}
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent" />

                <div className="absolute top-3 left-3">
                  <div
                    className={cn(
                      'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold shadow-sm',
                      meta.badge,
                    )}
                  >
                    <span className="text-sm leading-none">{meta.medal}</span>
                    <span>{meta.label}</span>
                  </div>
                </div>
                <div className="absolute top-3 right-3">
                  <span className="rounded-full bg-background/70 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-foreground backdrop-blur-sm">
                    #{rank} of {results.length}
                  </span>
                </div>

                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <h3 className="font-serif text-2xl font-semibold text-foreground leading-tight">
                    {dest.name}
                  </h3>
                  <p className="text-xs text-muted-foreground mt-0.5">{dest.country}</p>
                </div>
              </div>
            </button>

            {/* Body */}
            <div className="p-4 space-y-4">
              <RecommendationExplanation destination={dest} preferences={preferences} />

              <Button
                type="button"
                onClick={() => onClick(dest, rank)}
                className="w-full h-12 text-base font-semibold"
              >
                View destination →
              </Button>
            </div>
          </article>
        );
      })}
    </div>
  );
};


